/** @jsx h */
import { h } from 'preact'
import CardStepBoutique from './card-step-boutique'
import CardStepDetails from './card-step-details'
import CardStepInformation from './card-step-information'
import CardStepVisit from './card-step-visit'
import Confirmation from './confirmation'
import Button from './button'
import { useLabel } from '../use-label'
import { useGlobal } from '../use-global'
import useAnalytics from '../use-analytics'
import { createNewAppointment, getRecaptchaKey, getTimeSlotsByDateAndType } from '../api'
import { useEffect, useState } from 'preact/compat'
import { ABBREVIATION_MAISON, EXTERNAL_POINT_OF_SALE } from '../constants'
import { sha256, validateRecaptcha } from '../utils'
import clsx from 'clsx'
import Icon from './icons/icons'

const AppointmentPage = ({ conf, countries, phoneValidation }) => {
  const [
    {
      purposeMessage,
      selectedBoutique,
      selectedPurpose,
      appointmentDetails,
      personalInfo,
      selectedCountry,
      steps,
      ready,
      canvasConfig,
      serialNumber
    },
    dispatch
  ] = useGlobal()
  const t = useLabel()
  const analytics = useAnalytics()

  const [submitting, setSubmitting] = useState(false)
  const [recaptchaKey, setRecaptchaKey] = useState('')
  const [hasError, setHasError] = useState(false)

  const isReadyToSubmit = Object.keys(steps).every(step => steps[step].done && !steps[step].open)

  const setStep = index => dispatch({ type: 'toggle-step', payload: { index } })

  useEffect(() => {
    const head = document.querySelector('head')
    const script = document.createElement('script')

    // Add reCaptcha
    const addReCaptcha = async () => {
      const recaptchaKey = await getRecaptchaKey({ apiBaseUrl: canvasConfig.apiBaseUrl })
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaKey}`
      head.appendChild(script)
      setRecaptchaKey(recaptchaKey)
    }

    addReCaptcha()

    return () => {
      head.removeChild(script)
    }
  }, [canvasConfig.apiBaseUrl])

  const submitHandler = async () => {
    if (submitting) {
      return
    }

    setSubmitting(true)

    try {
      const recaptchaToken = await validateRecaptcha(recaptchaKey)

      if (!recaptchaToken) {
        throw new Error('missing-reCAPTCHA-token')
      }

      if (selectedBoutique.boutiqueType !== EXTERNAL_POINT_OF_SALE) {
        // Check if the timeslot selected is still available
        const timeslots = await getTimeSlotsByDateAndType({
          apiBaseUrl: canvasConfig.apiBaseUrl,
          boutiqueId: selectedBoutique.id,
          date: appointmentDetails.AD_Date,
          type: selectedPurpose.assetType
        })

        const availableSlot = timeslots?.find(el => el.startT.substring(0, 5) === appointmentDetails.AD_Available_Time)

        if (!availableSlot) {
          throw new Error('slot-taken')
        }
      }

      const userCountry = countries.find(
        country => country.iso2Code === personalInfo.PI_Country_Of_Residence
      )?.originalName
      const boutiqueCountry = countries.find(
        country => country.iso2Code === selectedBoutique.countryOfOrigin
      )?.originalName

      const language =
        conf.enabledLanguages
          .find(lang => lang.value === appointmentDetails.AD_Preferred_Language)
          ?.iso2Code?.toLowerCase() || ''

      // Data to search from a customer
      const searchCustomerData = {
        firstName: personalInfo.PI_FirstName,
        lastName: personalInfo.PI_LastName,
        country: personalInfo.PI_Country_Of_Residence,
        maison: ABBREVIATION_MAISON[canvasConfig.maisonId]
      }

      // Data to create a customer
      const createCustomerData = {
        creatingBoutiqueId: selectedBoutique.externalId,
        maison: ABBREVIATION_MAISON[canvasConfig.maisonId],
        countryOfOrigin: boutiqueCountry,
        title: personalInfo.PI_Title,
        gender: personalInfo.PI_Title === '0002' ? 'Male' : 'Female',
        localFirstName: personalInfo.PI_FirstName,
        localLastName: personalInfo.PI_LastName,
        latinFirstName: personalInfo.PI_OtherFirstName,
        latinLastName: personalInfo.PI_OtherLastName,
        spokenLanguage: language,
        writtenLanguage: language,
        partnerCategory: '1',
        fullName: personalInfo.PI_FullName,
        dataOrigin: 'RDV',
        dataSuborigin: 'WEB',
        enforceCustomerPropagation: true,
        electronicContact: Boolean(personalInfo.PI_Consent_SendMarketingCom),
        voiceContact: Boolean(personalInfo.PI_Consent_SendMarketingCom),
        mmsContact: Boolean(personalInfo.PI_Consent_SendMarketingCom),
        socialContact: Boolean(personalInfo.PI_Consent_SendMarketingCom),
        postalContact: Boolean(personalInfo.PI_Consent_SendMarketingCom),
        addresses: [
          {
            country: userCountry,
            grMainAddress: true,
            countryOfOrigin: boutiqueCountry,
            addressType: 'BU'
          }
        ]
      }

      // Data to create an appointment
      const appointmentData = {
        startDate: appointmentDetails.AD_Date,
        startTime: appointmentDetails.AD_Available_Time ? appointmentDetails.AD_Available_Time + ':00.000Z' : 'null', // it needs to be null as a string
        purposeOfVisit: selectedPurpose.value || null,
        comments: appointmentDetails.AD_Comments,
        internalComments: canvasConfig.internalComments || null,
        language: appointmentDetails.AD_Preferred_Language,
        isVirtual: appointmentDetails.AD_Appointment_Type === 'VIRTUAL',
        communicationChannel: appointmentDetails.AD_Communication_Channel || null,
        notificationChannel: personalInfo.PI_Notification_Channel,
        pickUpTypeValue: selectedPurpose.pickUpType,
        customerNationalId: personalInfo.PI_Id_Number || null,
        POVSerialNumber: serialNumber,
        externalRequestTime: appointmentDetails.AD_Preferred_Time || null,
        externalPurposeOfVisit: purposeMessage || null,
        source: canvasConfig.source ?? '',
        productReference:
          canvasConfig.context === 'productdiscovery' && canvasConfig.product.reference
            ? canvasConfig.product.reference
            : null
      }

      // add email address to each data object if exists
      if (personalInfo.PI_Email_Address) {
        const email = personalInfo.PI_Email_Address

        searchCustomerData.email = email
        createCustomerData.emails = [
          {
            emailAddress: email,
            emailType: '1',
            defaultGrEmail: true,
            countryOfOrigin: boutiqueCountry
          }
        ]
        appointmentData.email = email
      }

      // add phone data to each data object if exists
      if (personalInfo.PI_Mobile_Number) {
        const { value: prefixValue, country: prefixCountry } = personalInfo.PI_Mobile_Prefix
        const phoneNumber = personalInfo.PI_Mobile_Number

        searchCustomerData.phone = prefixValue + phoneNumber
        createCustomerData.phones = [
          {
            phoneNumber,
            phoneType: 'BU',
            defaultGrPhone: true,
            phoneCountry: prefixCountry,
            countryOfOrigin: boutiqueCountry
          }
        ]
        appointmentData.phone = prefixValue + phoneNumber
      }

      // If the timeslot is available then proceed with new appointment creation
      await createNewAppointment({
        apiBaseUrl: canvasConfig.apiBaseUrl,
        boutiqueId: selectedBoutique.id,
        body: {
          recaptchaToken,
          searchCustomer: searchCustomerData,
          createCustomer: createCustomerData,
          appointment: appointmentData
        }
      }).catch(err => {
        console.log('New appointment creation failed:\n', err)
        throw new Error('new-appointment-failed')
      })

      // Everything went well.
      // Add analytic data and mark ready as true to proceed to confirmation page
      setSubmitting(false)

      dispatch({ type: 'set-ready' })
      analytics.trackConfirmation({
        boutiqueId: selectedBoutique.id,
        selectedCountry,
        purpose: selectedPurpose.value || null,
        boutiqueName: selectedBoutique.commercialName || selectedBoutique.name,
        type: appointmentDetails.AD_Appointment_Type === 'VIRTUAL' ? 'virtual' : 'in_store',
        prefLanguage: appointmentDetails.AD_Preferred_Language,
        channel: appointmentDetails.AD_Communication_Channel || null,
        email: await sha256(personalInfo.PI_Email_Address)
      })
    } catch (err) {
      setSubmitting(false)

      if (err.message === 'slot-taken') {
        dispatch({ type: 'slot-taken' })
        canvasConfig.onSectionExpanded({
          selectorClassName: 'rcc_c_card-step-details',
          index: 2
        })

        alert('An error has occurred, please try again.')
        return
      }

      if (err.message === 'missing-reCAPTCHA-token') {
        console.error('Unable to obtain reCAPTCHA token')
      }

      if (err.message === 'invalid-reCAPTCHA-key') {
        console.error('Invalid reCAPTCHA key')
      }

      setHasError(true)
    }
  }

  return ready ? (
    <Confirmation />
  ) : (
    <div className='rcc_c_page-appointment rcc-font-widget'>
      <h2
        className={clsx(
          'rcc_c_page-appointment-title rcc-text-[32px] rcc-font-medium rcc-text-center rcc-text-text rcc-uppercase',
          { 'rcc-tracking-wider': canvasConfig.locale !== 'ar' }
        )}
      >
        {t('Global.Request_Appointment')}
      </h2>

      <div className='rcc_c_page-appointment-content'>
        {steps[0].done && !steps[0].open && (
          <h3
            className={clsx(
              'rcc-uppercase rcc-font-normal rcc-text-gray rcc-max-w-[760px] rcc-mx-auto rcc-border-t rcc-border-gray-very-light rcc-py-[18px] mobile:rcc-border-0',
              {
                'rcc-text-right': canvasConfig.direction === 'rtl',
                'rcc-tracking-[1.5px]': canvasConfig.locale !== 'ar'
              }
            )}
          >
            {t('Global.Selected_Appointment')}
          </h3>
        )}
        <CardStepBoutique
          selectorClassName='rcc_c_card-step-boutique'
          conf={conf}
          isOpen={steps[0].open}
          isDone={steps[0].done}
          isEnabled={steps[0].enabled}
          isHighlighted={steps[0].highlighted}
          onToggle={index => {
            if (!steps[0].open) {
              canvasConfig.onSectionExpanded({
                selectorClassName: 'rcc_c_card-step-boutique',
                index
              })
            }
            setStep(index)
          }}
        />
        <CardStepVisit
          selectorClassName='rcc_c_card-step-visit'
          isOpen={steps[1].open}
          isDone={steps[1].done}
          isEnabled={steps[1].enabled}
          isHighlighted={steps[1].highlighted}
          onToggle={index => {
            if (!steps[1].open) {
              canvasConfig.onSectionExpanded({
                selectorClassName: 'rcc_c_card-step-visit',
                index
              })
            }
            setStep(index)
          }}
        />
        <CardStepDetails
          selectorClassName='rcc_c_card-step-details'
          isOpen={steps[2].open}
          isDone={steps[2].done}
          isEnabled={steps[2].enabled}
          isHighlighted={steps[2].highlighted}
          onToggle={index => {
            if (!steps[3].open) {
              canvasConfig.onSectionExpanded({
                selectorClassName: 'rcc_c_card-step-details',
                index
              })
            }
            setStep(index)
          }}
        />
        <CardStepInformation
          selectorClassName='rcc_c_card-step-information'
          phoneValidation={phoneValidation}
          countries={countries}
          isOpen={steps[3].open}
          isDone={steps[3].done}
          isEnabled={steps[3].enabled}
          isHighlighted={steps[3].highlighted}
          onToggle={index => {
            if (!steps[3].open) {
              canvasConfig.onSectionExpanded({
                selectorClassName: 'rcc_c_card-step-information',
                index
              })
            }
            setStep(index)
          }}
        />

        {isReadyToSubmit && (
          <div className='rcc_c_wrapper-btn-send-request rcc-max-w-[560px] rcc-mx-auto rcc-mt-6'>
            {selectedBoutique.boutiqueType === EXTERNAL_POINT_OF_SALE && (
              <div className='rcc-text-text rcc-text-center rcc-m-8 rcc-font-extralight'>
                {t('Submit.Submit_Data_Sharing_With_External')}
              </div>
            )}
            <div className='rcc_c_wrapper-btn-send-request-wrapper'>
              {hasError ? (
                <div className='rcc-bg-danger rcc-h-[50px] rcc-pl-5 rcc-flex rcc-justify-between rcc-items-center rcc-gap-2'>
                  <p className='rcc-text-white'>Oops! Something went wrong...</p>
                  <button
                    type='button'
                    onClick={() => setHasError(false)}
                    aria-label='Close error message'
                    className={
                      'rcc-bg-transparent rcc-cursor-pointer rcc-h-full rcc-w-[50px] rcc-border-solid rcc-border-0 rcc-border-l-2 rcc-border-white rcc-flex rcc-justify-center rcc-items-center'
                    }
                  >
                    <Icon name='close' height={18} width={18} stroke={'#fff'} fill={'#fff'} />
                  </button>
                </div>
              ) : (
                <Button loading={submitting} onClick={submitHandler} disabled={submitting}>
                  {t('Global.Send_Request')}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AppointmentPage
